import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CardLink } from '../../components/CardLink';
import {
    getEntity, getEntityTasks, getAllEntities, getChildEntities,
    getTaskLinks, linkTaskToEntity, unlinkTask, createEntity, createTaskForEntity,
    deleteEntity,
    type Entity, type EntityTask, type TaskLink, type EntityType,
    getEntityTypes,
} from '../../packages/api-client/entities-api';
import { getProjects, createProject, getEntityProjects, deleteProject, getUsers, type User, type Project } from '../../packages/api-client/pm-api';
import { EntityCustomFields } from '../../components/EntityCustomFields';
import { RelatedRecords } from '../../components/RelatedRecords';
import { formatDueDate } from '../../utils/date';
import { hexAlpha } from '../../utils/statusColors';
import { usePageTitle } from '../../hooks/usePageTitle';
import { StateView } from '../../packages/ui';
import { PriorityIcon, StatusIcon, EntityIcon, PICKER_ICONS } from '../../components/taskIcons';
import { FolderTree, Trash2, Rocket, ClipboardList, Users, Calendar, Link2, X } from 'lucide-react';

const PRIORITY_OPTIONS = ['urgent', 'high', 'medium', 'low', 'none'];

export function EntityDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [entity, setEntity] = useState<Entity | null>(null);
    usePageTitle(entity?.name ?? 'Record');
    const [children, setChildren] = useState<Entity[]>([]);
    const [tasks, setTasks] = useState<EntityTask[]>([]);
    const [breadcrumb, setBreadcrumb] = useState<Entity[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<'not-found' | 'failed' | null>(null);

    // Modals
    const [showAddChild, setShowAddChild] = useState(false);
    const [showAddTask, setShowAddTask] = useState(false);
    const [showAddProject, setShowAddProject] = useState(false);
    const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
    const [childForm, setChildForm] = useState({ entity_type_id: '', name: '', description: '' });
    const [taskForm, setTaskForm] = useState({ title: '', description: '', priority: 'medium', due_date: '', assignee_id: '' });
    const [projectForm, setProjectForm] = useState({ name: '', description: '', color: '#1A9E8F', icon: 'folder', status: 'active' });
    const [projects, setProjects] = useState<Project[]>([]);
    const [projectsLoaded, setProjectsLoaded] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [creating, setCreating] = useState(false);
    const [firstProjectId, setFirstProjectId] = useState<string | null>(null);
    const [resolvingProject, setResolvingProject] = useState(false);

    // Link task helper
    const [allEntities, setAllEntities] = useState<Entity[]>([]);
    const [showLinkPicker, setShowLinkPicker] = useState(false);
    const [linkSearch, setLinkSearch] = useState('');

    const load = useCallback(() => {
        if (!id) return;
        setLoading(true);
        setLoadError(null);
        Promise.allSettled([
            getEntity(id),
            getEntityTasks(id),
            getChildEntities(id),
            getEntityProjects(id),
        ]).then(([eR, tR, cR, pR]) => {
            if (eR.status === 'fulfilled') {
                setEntity(eR.value);
                buildBreadcrumb(eR.value);
            } else {
                setEntity(null);
                const status = (eR.reason as { response?: { status?: number } })?.response?.status;
                setLoadError(status === 404 ? 'not-found' : 'failed');
            }
            if (tR.status === 'fulfilled') setTasks(tR.value);
            if (cR.status === 'fulfilled') setChildren(cR.value);
            if (pR.status === 'fulfilled') { setProjects(pR.value); setProjectsLoaded(true); }
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [id]);
    useEffect(load, [load]);

    async function buildBreadcrumb(e: Entity) {
        const crumbs: Entity[] = [];
        let cur = e;
        while (cur.parent_id) {
            const parent = await getEntity(cur.parent_id);
            crumbs.unshift(parent);
            cur = parent;
        }
        setBreadcrumb(crumbs);
    }

    async function openAddChild() {
        if (entityTypes.length === 0) setEntityTypes(await getEntityTypes());
        setShowAddChild(true);
    }

    async function openAddTask() {
        setResolvingProject(true);
        setShowAddTask(true);
        try {
            if (!firstProjectId) {
                // Use entity's own linked projects first
                const ep = projectsLoaded ? projects : await getEntityProjects(id!);
                if (ep.length > 0) {
                    setFirstProjectId(ep[0].id);
                } else {
                    const res = await getProjects(1, 1);
                    if (res.data?.[0]) {
                        setFirstProjectId(res.data[0].id);
                    } else {
                        const autoProject = await createProject({
                            name: entity?.name ? `${entity.name} — Tasks` : 'General Tasks',
                            description: 'Auto-created project for entity tasks',
                            color: '#1A9E8F',
                            icon: 'folder',
                            status: 'active',
                            entity_id: id,
                        });
                        setFirstProjectId(autoProject.id);
                        setProjects(prev => [autoProject, ...prev]);
                    }
                }
            }
            if (users.length === 0) {
                getUsers().then(setUsers).catch(() => { });
            }
        } finally {
            setResolvingProject(false);
        }
    }

    async function handleCreateProject(ev: React.FormEvent) {
        ev.preventDefault();
        if (!id) return;
        setCreating(true);
        try {
            const p = await createProject({
                name: projectForm.name,
                description: projectForm.description,
                color: projectForm.color,
                icon: projectForm.icon,
                status: projectForm.status as 'active' | 'planning',
                entity_id: id,
            });
            setProjects(prev => [p, ...prev]);
            setFirstProjectId(prev => prev ?? p.id);
            setShowAddProject(false);
            setProjectForm({ name: '', description: '', color: '#1A9E8F', icon: 'folder', status: 'active' });
        } finally { setCreating(false); }
    }

    async function handleCreateChild(ev: React.FormEvent) {
        ev.preventDefault();
        setCreating(true);
        try {
            const child = await createEntity({ ...childForm, parent_id: id });
            setChildren(prev => [child, ...prev]);
            setShowAddChild(false);
            setChildForm({ entity_type_id: '', name: '', description: '' });
        } finally { setCreating(false); }
    }

    async function handleDeleteEntity() {
        if (!entity || !id) return;
        if (!window.confirm(`Delete entity "${entity.name}"? All its tasks and child records will be unlinked. This cannot be undone.`)) return;
        await deleteEntity(id);
        navigate(entity.parent_id ? `/entities/${entity.parent_id}` : '/entities');
    }

    async function handleDeleteProject(proj: Project) {
        if (!window.confirm(`Delete project "${proj.name}"? This cannot be undone.`)) return;
        await deleteProject(proj.id);
        setProjects(prev => prev.filter(p => p.id !== proj.id));
        if (firstProjectId === proj.id) setFirstProjectId(null);
    }

    async function handleDeleteChild(child: Entity) {
        if (!window.confirm(`Delete "${child.name}"? This cannot be undone.`)) return;
        await deleteEntity(child.id);
        setChildren(prev => prev.filter(c => c.id !== child.id));
    }

    async function handleCreateTask(ev: React.FormEvent) {
        ev.preventDefault();
        if (!firstProjectId || !id) return;
        setCreating(true);
        try {
            const task = await createTaskForEntity(id, firstProjectId, {
                title: taskForm.title,
                description: taskForm.description,
                priority: taskForm.priority,
                due_date: taskForm.due_date || undefined,
                assignee_id: taskForm.assignee_id || undefined,
            });
            setTasks(prev => [{ ...task, assignee_id: taskForm.assignee_id || undefined } as EntityTask, ...prev]);
            setShowAddTask(false);
            setTaskForm({ title: '', description: '', priority: 'medium', due_date: '', assignee_id: '' });
        } finally { setCreating(false); }
    }

    // Link existing task
    const filteredLink = allEntities.filter(e =>
        e.name.toLowerCase().includes(linkSearch.toLowerCase()) ||
        e.entity_type_name.toLowerCase().includes(linkSearch.toLowerCase())
    );

    const grouped = {
        todo: tasks.filter(t => t.status === 'todo'),
        in_progress: tasks.filter(t => t.status === 'in_progress'),
        blocked: tasks.filter(t => t.status === 'blocked'),
        review: tasks.filter(t => t.status === 'review'),
        done: tasks.filter(t => t.status === 'done'),
    };

    const isOverdue = (due?: string) => due && new Date(due) < new Date();

    if (loading) return <StateView state="loading" label="Loading record" lines={6} />;
    if (!entity) {
        return loadError === 'failed'
            ? <StateView state="error" message="This record could not be loaded. Check your connection and retry." onRetry={load} />
            : <StateView state="empty" title="Record not found"
                message="It may have been deleted, or the link is wrong."
                action={<button type="button" className="btn btn-secondary btn-md" onClick={() => navigate('/entities')}>Back to records</button>} />;
    }

    return (
        <div>
            {/* Breadcrumb */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '1rem', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }} onClick={() => navigate('/entities')}><FolderTree size={14} aria-hidden="true" /> Records</button>
                {breadcrumb.map(crumb => (
                    <React.Fragment key={crumb.id}>
                        <span>›</span>
                        <button className="btn btn-ghost btn-sm" style={{ padding: '0 4px', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }} onClick={() => navigate(`/entities/${crumb.id}`)}>
                            <EntityIcon icon={crumb.entity_type_icon} size={12} /> {crumb.name}
                        </button>
                    </React.Fragment>
                ))}
                <span>›</span>
                <span style={{ fontWeight: 600, color: 'var(--color-text)', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><EntityIcon icon={entity.entity_type_icon} size={12} /> {entity.name}</span>
            </div>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                    <div style={{ width: '3.5rem', height: '3.5rem', borderRadius: '1rem', background: hexAlpha(entity.entity_type_color, 0.13), color: entity.entity_type_color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem' }}>
                        <EntityIcon icon={entity.entity_type_icon} size={28} />
                    </div>
                    <div>
                        <h1 className="page-title" style={{ marginBottom: '0.25rem' }}>{entity.name}</h1>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: entity.entity_type_color, background: hexAlpha(entity.entity_type_color, 0.13), padding: '2px 10px', borderRadius: 99, display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                <EntityIcon icon={entity.entity_type_icon} size={11} /> {entity.entity_type_name}
                            </span>
                            {entity.description && <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>{entity.description}</span>}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button className="btn btn-ghost btn-md" onClick={openAddChild}>+ Add Child</button>
                    <button className="btn btn-secondary btn-md" onClick={() => setShowAddProject(true)}>+ Add Project</button>
                    <button className="btn btn-primary btn-md" onClick={openAddTask}>+ New Task</button>
                    <button
                        onClick={handleDeleteEntity}
                        title="Delete this entity"
                        style={{
                            background: 'none', border: '1px solid var(--color-danger)', color: 'var(--color-danger)',
                            borderRadius: 'var(--radius-md)', padding: '0.4rem 0.75rem', cursor: 'pointer',
                            fontSize: 'var(--font-size-sm)', display: 'flex', alignItems: 'center', gap: '0.35rem',
                        }}
                    >
                        <Trash2 size={14} aria-hidden="true" /> Delete
                    </button>
                </div>
            </div>

            {/* Task stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
                {(['todo', 'in_progress', 'blocked', 'review', 'done'] as const).map(s => (
                    <div key={s} className="card" style={{ textAlign: 'center' }}>
                        <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'center' }}><StatusIcon status={s} size={20} /></div>
                        <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>{grouped[s].length}</div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'capitalize' }}>{s.replace('_', ' ')}</div>
                    </div>
                ))}
            </div>

            {/* Custom Fields */}
            {entity.entity_type_id && (
                <div style={{ marginBottom: 'var(--space-6)' }}>
                    <EntityCustomFields
                        entityId={entity.id}
                        entityTypeId={entity.entity_type_id}
                        metadata={(entity.metadata as Record<string, unknown>) ?? {}}
                        onMetadataChange={(updated) => setEntity(prev => prev ? { ...prev, metadata: updated as typeof prev.metadata } : prev)}
                    />
                </div>
            )}

            {/* Child Entities */}
            {(children.length > 0 || entity.child_count > 0) && (
                <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
                    <div style={{ fontWeight: 700, marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}><FolderTree size={16} aria-hidden="true" /> Child Records ({children.length})</span>
                        <button className="btn btn-ghost btn-sm" onClick={openAddChild}>+ Add Child</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
                        {children.map(child => (
                            <CardLink key={child.id}
                                to={`/entities/${child.id}`}
                                ariaLabel={child.name}
                                className="project-card"
                                style={{ cursor: 'pointer', position: 'relative', padding: '0.875rem' }}>
                                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: child.entity_type_color, borderRadius: '0.5rem 0.5rem 0 0' }} />
                                {/* Delete child button */}
                                <button
                                    onClick={e => { e.stopPropagation(); handleDeleteChild(child); }}
                                    title="Delete child"
                                    aria-label="Delete child"
                                    style={{
                                        position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none',
                                        border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)',
                                        fontSize: '0.85rem', padding: '2px 5px', borderRadius: 'var(--radius-sm)',
                                        lineHeight: 1, zIndex: 2,   /* above the CardLink overlay */
                                    }}
                                >
                                    <Trash2 size={14} aria-hidden="true" />
                                </button>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem' }}>
                                    <div style={{ width: '2rem', height: '2rem', borderRadius: '0.5rem', background: hexAlpha(child.entity_type_color, 0.13), color: child.entity_type_color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <EntityIcon icon={child.entity_type_icon} size={15} />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{child.name}</div>
                                        <div style={{ fontSize: 'var(--font-size-xs)', color: child.entity_type_color, fontWeight: 600 }}>{child.entity_type_name}</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.75rem', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><ClipboardList size={12} aria-hidden="true" /> {child.task_count} task{child.task_count !== 1 ? 's' : ''}</span>
                                    {child.child_count > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><FolderTree size={12} aria-hidden="true" /> {child.child_count} sub-records</span>}
                                </div>
                            </CardLink>
                        ))}
                    </div>
                </div>
            )}

            {/* Projects */}
            <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{ fontWeight: 700, marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}><Rocket size={16} aria-hidden="true" /> Projects ({projects.length})</span>
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowAddProject(true)}>+ Add Project</button>
                </div>
                {projects.length === 0 ? (
                    <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: '1.5rem 0' }}>
                        No projects yet. Add a project to start tracking tasks.
                    </p>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.75rem' }}>
                        {projects.map(proj => (
                            <CardLink key={proj.id}
                                to={`/projects/${proj.id}`}
                                ariaLabel={proj.name}
                                className="project-card"
                                style={{ cursor: 'pointer', position: 'relative', padding: '0.875rem' }}>
                                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: proj.color, borderRadius: '0.5rem 0.5rem 0 0' }} />
                                {/* Delete project button */}
                                <button
                                    onClick={e => { e.stopPropagation(); handleDeleteProject(proj); }}
                                    title="Delete project"
                                    aria-label="Delete project"
                                    style={{
                                        position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none',
                                        border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)',
                                        fontSize: '0.85rem', padding: '2px 5px', borderRadius: 'var(--radius-sm)',
                                        lineHeight: 1, zIndex: 2,   /* above the CardLink overlay */
                                    }}
                                >
                                    <Trash2 size={14} aria-hidden="true" />
                                </button>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                                    <div style={{ width: '2rem', height: '2rem', borderRadius: '0.5rem', background: hexAlpha(proj.color, 0.13), color: proj.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <EntityIcon icon={proj.icon} size={15} />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{proj.name}</div>
                                        <span style={{ fontSize: 'var(--font-size-xs)', background: hexAlpha(proj.color, 0.13), color: proj.color, padding: '1px 7px', borderRadius: 99, textTransform: 'capitalize' }}>
                                            {proj.status.replace('_', ' ')}
                                        </span>
                                    </div>
                                </div>
                                {proj.description && (
                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: '0.4rem' }}>{proj.description}</div>
                                )}
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <Users size={12} aria-hidden="true" /> {proj.member_count} member{proj.member_count !== 1 ? 's' : ''}
                                </div>
                            </CardLink>
                        ))}
                    </div>
                )}
            </div>

            {/* Tasks */}
            <div className="card">
                <div style={{ fontWeight: 700, marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}><ClipboardList size={16} aria-hidden="true" /> Tasks ({tasks.length})</span>
                    <button className="btn btn-ghost btn-sm" onClick={openAddTask}>+ New Task</button>
                </div>
                {tasks.length === 0
                    ? <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: '2rem 0' }}>
                        No tasks yet. Click "+ New Task" to add one.
                    </p>
                    : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                            {tasks.map(task => (
                                <CardLink key={task.id}
                                    to={`/tasks/${task.id}`} ariaLabel={task.title}
                                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)', cursor: 'pointer', border: '1px solid var(--color-border)', transition: 'border-color 0.15s' }}>
                                    <span style={{ display: 'inline-flex' }}><StatusIcon status={task.status} size={16} /></span>
                                    <span style={{ flex: 1, fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>{task.title}</span>
                                    <span className={`priority-badge priority-${task.priority}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><PriorityIcon priority={task.priority} /> {task.priority}</span>
                                    {task.due_date && (
                                        <span style={{ fontSize: 'var(--font-size-xs)', color: isOverdue(task.due_date) ? 'var(--color-danger)' : 'var(--color-text-muted)', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                            <Calendar size={12} aria-hidden="true" /> {formatDueDate(task.due_date)}
                                        </span>
                                    )}
                                </CardLink>
                            ))}
                        </div>
                    )
                }
            </div>

            {/* Add Project Modal */}
            {showAddProject && (
                <div className="modal-overlay" onClick={() => setShowAddProject(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <span className="modal-title" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}><Rocket size={16} aria-hidden="true" /> New Project</span>
                            <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setShowAddProject(false)}><X size={16} aria-hidden="true" /></button>
                        </div>
                        <form onSubmit={handleCreateProject}>
                            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', padding: '0.5rem 0.75rem', background: hexAlpha(entity.entity_type_color, 0.09), borderRadius: 'var(--radius-md)', borderLeft: `3px solid ${entity.entity_type_color}` }}>
                                    Project under: <EntityIcon icon={entity.entity_type_icon} size={13} /> <strong>{entity.name}</strong>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Project Name *</label>
                                    <input className="form-input" required autoFocus placeholder="e.g. Q2 Campaign" value={projectForm.name} onChange={e => setProjectForm(f => ({ ...f, name: e.target.value }))} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <input className="form-input" placeholder="What is this project about?" value={projectForm.description} onChange={e => setProjectForm(f => ({ ...f, description: e.target.value }))} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div className="form-group">
                                        <label className="form-label">Status</label>
                                        <select className="form-input" value={projectForm.status} onChange={e => setProjectForm(f => ({ ...f, status: e.target.value }))}>
                                            <option value="planning">Planning</option>
                                            <option value="active">Active</option>
                                            <option value="on_hold">On Hold</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Color</label>
                                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', paddingTop: '0.25rem' }}>
                                            {['#2DE8D0', '#14867A', '#F5A623', '#F97316', '#F87171', '#C05CFF', '#3BA7FF', '#94A3B8'].map(c => (
                                                <button key={c} type="button"
                                                    style={{ width: '1.5rem', height: '1.5rem', borderRadius: '50%', background: c, border: '3px solid transparent', cursor: 'pointer', outline: projectForm.color === c ? '2px solid var(--ink)' : 'none', outlineOffset: 2 }}
                                                    onClick={() => setProjectForm(f => ({ ...f, color: c }))} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary btn-md" type="button" onClick={() => setShowAddProject(false)}>Cancel</button>
                                <button className="btn btn-primary btn-md" type="submit" disabled={creating}>{creating ? 'Creating…' : 'Create Project'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Child Modal */}
            {showAddChild && (
                <div className="modal-overlay" onClick={() => setShowAddChild(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <span className="modal-title">Add Child Record</span>
                            <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setShowAddChild(false)}><X size={16} aria-hidden="true" /></button>
                        </div>
                        <form onSubmit={handleCreateChild}>
                            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', padding: '0.5rem 0.75rem', background: hexAlpha(entity.entity_type_color, 0.09), borderRadius: 'var(--radius-md)', borderLeft: `3px solid ${entity.entity_type_color}` }}>
                                    Adding child to: <EntityIcon icon={entity.entity_type_icon} size={13} /> <strong>{entity.name}</strong>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Type *</label>
                                    <select className="form-input" required value={childForm.entity_type_id} onChange={e => setChildForm(f => ({ ...f, entity_type_id: e.target.value }))}>
                                        <option value="">— Select type —</option>
                                        {entityTypes.map(t => <option key={t.id} value={t.id}>{PICKER_ICONS[t.icon] ? t.name : `${t.icon} ${t.name}`}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Name *</label>
                                    <input className="form-input" required placeholder="e.g. Website Redesign" value={childForm.name} onChange={e => setChildForm(f => ({ ...f, name: e.target.value }))} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <input className="form-input" placeholder="Optional" value={childForm.description} onChange={e => setChildForm(f => ({ ...f, description: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary btn-md" type="button" onClick={() => setShowAddChild(false)}>Cancel</button>
                                <button className="btn btn-primary btn-md" type="submit" disabled={creating}>{creating ? 'Creating…' : 'Create Record'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Task Modal */}
            {showAddTask && (
                <div className="modal-overlay" onClick={() => setShowAddTask(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <span className="modal-title">New Task</span>
                            <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setShowAddTask(false)}><X size={16} aria-hidden="true" /></button>
                        </div>
                        {resolvingProject ? (
                            <div className="modal-body" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                                <div className="spinner spinner-md" />
                            </div>
                        ) : (
                            <form onSubmit={handleCreateTask}>
                                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <div className="form-group">
                                        <label className="form-label">Title *</label>
                                        <input className="form-input" required autoFocus placeholder="What needs to be done?" value={taskForm.title} onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Description</label>
                                        <input className="form-input" placeholder="Optional details" value={taskForm.description} onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                        <div className="form-group">
                                            <label className="form-label">Priority</label>
                                            <select className="form-input" value={taskForm.priority} onChange={e => setTaskForm(f => ({ ...f, priority: e.target.value }))}>
                                                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Due Date</label>
                                            <input className="form-input" type="date" value={taskForm.due_date} onChange={e => setTaskForm(f => ({ ...f, due_date: e.target.value }))} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Assignee</label>
                                        <select className="form-input" value={taskForm.assignee_id} onChange={e => setTaskForm(f => ({ ...f, assignee_id: e.target.value }))}>
                                            <option value="">— Unassigned —</option>
                                            {users.map(u => (
                                                <option key={u.id} value={u.id}>
                                                    {u.first_name || u.last_name ? `${u.first_name} ${u.last_name}`.trim() : u.email}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary btn-md" type="button" onClick={() => setShowAddTask(false)}>Cancel</button>
                                <button className="btn btn-primary btn-md" type="submit" disabled={creating}>{creating ? 'Creating…' : 'Create Task'}</button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
            <RelatedRecords entityId={id!} />
        </div>
    );
}

// ─── TaskLinkedTo — embedded in TaskDetailPage sidebar ────────────────────────

interface TaskLinkedToProps { taskId: string; }

export function TaskLinkedTo({ taskId }: TaskLinkedToProps) {
    const navigate = useNavigate();
    const [links, setLinks] = useState<TaskLink[]>([]);
    const [allEntities, setAllEntities] = useState<Entity[]>([]);
    const [showPicker, setShowPicker] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => { getTaskLinks(taskId).then(setLinks); }, [taskId]);
    useEffect(() => { if (showPicker) getAllEntities().then(setAllEntities); }, [showPicker]);

    async function handleLink(entityId: string) {
        await linkTaskToEntity(taskId, entityId);
        const updated = await getTaskLinks(taskId);
        setLinks(updated);
        setShowPicker(false);
    }

    async function handleUnlink(linkId: string) {
        await unlinkTask(taskId, linkId);
        setLinks(prev => prev.filter(l => l.id !== linkId));
    }

    const filtered = allEntities.filter(e =>
        e.name.toLowerCase().includes(search.toLowerCase()) ||
        e.entity_type_name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="task-sidebar-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}><Link2 size={14} aria-hidden="true" /> Linked To</div>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowPicker(p => !p)}>+ Link</button>
            </div>

            {links.length === 0 && !showPicker && (
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', margin: 0 }}>Not linked to any record</p>
            )}

            {links.map(link => (
                <CardLink key={link.id} to={`/entities/${link.entity_id}`} ariaLabel={link.entity_name} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
                    <span style={{ color: link.entity_type_color, fontSize: '0.875rem' }}><EntityIcon icon={link.entity_type_icon} size={14} /></span>
                    <span style={{ flex: 1, fontWeight: 500 }}>{link.entity_name}</span>
                    <span style={{ fontSize: 'var(--font-size-xs)', background: hexAlpha(link.entity_type_color, 0.13), color: link.entity_type_color, padding: '1px 6px', borderRadius: 99 }}>{link.entity_type_name}</span>
                    <button className="btn btn-ghost btn-sm" aria-label="Unlink" style={{ color: 'var(--color-danger)', padding: '0 4px', position: 'relative', zIndex: 2 }} onClick={e => { e.stopPropagation(); handleUnlink(link.id); }}><X size={14} aria-hidden="true" /></button>
                </CardLink>
            ))}

            {showPicker && (
                <div style={{ marginTop: 'var(--space-2)' }}>
                    <input className="form-input" style={{ marginBottom: 'var(--space-2)', height: '2rem', fontSize: 'var(--font-size-sm)' }}
                        autoFocus placeholder="Search records…" value={search} onChange={e => setSearch(e.target.value)} />
                    <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {filtered.slice(0, 20).map(e => (
                            <button key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', cursor: 'pointer', textAlign: 'left', width: '100%', fontSize: 'var(--font-size-sm)' }}
                                onClick={() => handleLink(e.id)}>
                                <span style={{ color: e.entity_type_color }}><EntityIcon icon={e.entity_type_icon} size={14} /></span>
                                <span style={{ flex: 1 }}>{e.name}</span>
                                <span style={{ fontSize: '0.65rem', color: e.entity_type_color, background: hexAlpha(e.entity_type_color, 0.13), padding: '1px 5px', borderRadius: 99 }}>{e.entity_type_name}</span>
                            </button>
                        ))}
                        {filtered.length === 0 && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', padding: '0.5rem' }}>No records found</p>}
                    </div>
                    <button className="btn btn-ghost btn-sm" style={{ marginTop: 4 }} onClick={() => setShowPicker(false)}>Cancel</button>
                </div>
            )}
        </div>
    );
}
